/**
 * Utility functions for handling numeric input fields in forms
 * Provides consistent behavior for text inputs that should handle numbers
 */

/**
 * Converts a value to string for display in text input
 * Handles undefined, null, and numeric values properly
 */
export const valueToString = (value: number | undefined | null): string => {
    if (value === undefined || value === null) {
        return '';
    }
    return String(value);
};

/**
 * Parses text input to number for form state
 * Handles empty string as 0 for required fields, undefined for optional
 */
export const parseInputToNumber = (
    text: string,
    isRequired: boolean = false
): number | undefined => {
    if (text === '') {
        return isRequired ? 0 : undefined;
    }
    
    const numValue = Number(text);
    return Number.isFinite(numValue) ? numValue : (isRequired ? 0 : undefined);
};

/**
 * Validates if a numeric value is valid (greater than 0 for prices)
 */
export const isValidNumericValue = (
    value: number | undefined | null,
    min: number = 0,
    allowZero: boolean = false
): boolean => {
    if (value === undefined || value === null) {
        return false;
    }
    
    if (!Number.isFinite(value)) {
        return false;
    }
    
    if (!allowZero && value <= min) {
        return false;
    }
    
    return value >= min;
};

/**
 * Creates a change handler for numeric text inputs
 * Returns a function that can be used directly in onChange events
 */
export const createNumericInputChangeHandler = (
    setter: (value: number | undefined) => void,
    isRequired: boolean = false
) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numericValue = parseInputToNumber(raw, isRequired);
        setter(numericValue);
    };
};

/**
 * Creates a change handler for money value fields (price, face value)
 * Handles both value and currency fields
 */
export const createMoneyValueChangeHandler = (
    setter: (field: keyof MoneyValueDto, value: string | number | undefined) => void,
    field: keyof MoneyValueDto,
    isRequired: boolean = false
) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        
        if (field === 'value') {
            // Price/face value field - handle as number
            const numericValue = parseInputToNumber(raw, isRequired);
            setter(field, numericValue);
        } else {
            // Currency field - keep as string
            setter(field, raw);
        }
    };
};

/**
 * Money value interface for type safety
 */
export interface MoneyValueDto {
    value: number;
    currencyCode: string;
}

/**
 * Creates initial money value state
 */
export const createInitialMoneyValue = (
    defaultCurrency: string,
    defaultValue: number = 0
): MoneyValueDto => ({
    value: defaultValue,
    currencyCode: defaultCurrency
});
